import { useEffect, useRef } from 'react';
import { useAppStore } from '@/stores/appStore';
import { isLG } from '@/utils/platformUtils';

export interface KeyboardHandlers {
  // 导航键
  up?: () => void;
  down?: () => void;
  left?: (keyCode?: number) => void;
  right?: (keyCode?: number) => void;

  // 确认和返回
  click?: () => void;
  longPress?: () => void;
  exit?: (event?: KeyboardEvent) => void;
  quit?: (event?: KeyboardEvent) => void;

  // 媒体控制
  play?: () => void;
  pause?: () => void;
  playAndPause?: () => void;
  stop?: () => void;
}

// 检查是否为 Foxxum 平台
const isFoxxum = (): boolean => {
  const channelId = useAppStore.getState().vChannelId;
  return channelId === '16602' || channelId === '16622';
};

export const useKeyboard = (handlers: KeyboardHandlers) => {
  const isPressingEnterRef = useRef(false);
  const pressTimerRef = useRef<NodeJS.Timeout | null>(null);
  const hasLongPressExecutedRef = useRef(false);
  const handlersRef = useRef(handlers);

  // 更新 handlers 引用，避免每次都重新绑定事件
  useEffect(() => {
    handlersRef.current = handlers;
  }, [handlers]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      const { keyCode } = event;
      const win = window as any;
      const currentHandlers = handlersRef.current;

      switch (keyCode) {
        // 返回键 (三星/LG)
        case 10009:
        case 461:
        case win.VK_BACK:
          currentHandlers.quit?.(event);
          break;

        // 退出键 (Backspace/ESC)
        case 8:
        case 27:
          currentHandlers.exit?.(event);
          break;

        // 上方向键
        case 38:
        case win.VK_UP:
          currentHandlers.up?.();
          break;

        // 下方向键
        case 40:
        case win.VK_DOWN:
          currentHandlers.down?.();
          break;

        // 左方向键 / 快退
        case 37:
        case 227:
        case 119:
        case win.VK_LEFT:
        case win.VK_REWIND:
          currentHandlers.left?.(keyCode);
          break;

        // 右方向键 / 快进
        case 39:
        case 228:
        case 118:
        case win.VK_RIGHT:
        case win.VK_FAST_FWD:
          currentHandlers.right?.(keyCode);
          break;

        // 暂停/停止键
        case 19:
        case 413:
        case 169:
        case win.VK_STOP:
        case win.VK_PAUSE:
          if (keyCode === 413 && (isFoxxum() || isLG())) {
            // Foxxum 和 LG 的停止键
            currentHandlers.stop?.();
          } else {
            currentHandlers.pause?.();
          }
          break;

        // 播放键
        case 415:
        case 179:
        case win.VK_PLAY:
          currentHandlers.play?.();
          break;

        // 播放/暂停切换键
        case 116:
        case win.VK_PLAY_PAUSE:
          currentHandlers.playAndPause?.();
          break;

        // 确认键 (Enter) - 支持长按检测
        case 13:
        case win.VK_ENTER:
          if (!isPressingEnterRef.current) {
            isPressingEnterRef.current = true;
            pressTimerRef.current = setTimeout(() => {
              if (isPressingEnterRef.current && !hasLongPressExecutedRef.current) {
                currentHandlers.longPress?.();
                hasLongPressExecutedRef.current = true;
              }
            }, 500); // 500ms 长按阈值
          }
          break;

        // Astro 停止键
        case 117:
          currentHandlers.stop?.();
          break;
      }
    };

    const handleKeyUp = (event: KeyboardEvent) => {
      const { keyCode } = event;
      const win = window as any;
      const currentHandlers = handlersRef.current;

      // 处理 Enter 键释放
      if (keyCode === 13 || keyCode === win.VK_ENTER) {
        isPressingEnterRef.current = false;

        if (pressTimerRef.current) {
          clearTimeout(pressTimerRef.current);
          pressTimerRef.current = null;
        }

        // 如果没有触发长按，则触发普通点击
        if (!hasLongPressExecutedRef.current) {
          currentHandlers.click?.();
        }

        hasLongPressExecutedRef.current = false;
      }
    };

    const handleMouseDown = (event: MouseEvent) => {
      // 只处理鼠标左键 (button === 0)
      if (event.button !== 0) return;
      const currentHandlers = handlersRef.current;

      if (!isPressingEnterRef.current) {
        isPressingEnterRef.current = true;
        pressTimerRef.current = setTimeout(() => {
          if (isPressingEnterRef.current && !hasLongPressExecutedRef.current) {
            currentHandlers.longPress?.();
            hasLongPressExecutedRef.current = true;
          }
        }, 500);
      }
    };

    const handleMouseUp = (event: MouseEvent) => {
      if (event.button !== 0) return;

      isPressingEnterRef.current = false;

      if (pressTimerRef.current) {
        clearTimeout(pressTimerRef.current);
        pressTimerRef.current = null;
      }

      hasLongPressExecutedRef.current = false;
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);
    window.addEventListener('mousedown', handleMouseDown);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('keyup', handleKeyUp);
      window.removeEventListener('mousedown', handleMouseDown);
      window.removeEventListener('mouseup', handleMouseUp);

      // 清理定时器
      if (pressTimerRef.current) {
        clearTimeout(pressTimerRef.current);
      }
    };
  }, []); // 只在组件挂载时绑定一次
};
